import React from 'react';
import { LayoutDashboardIcon, MapPinIcon, ClipboardListIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, MapIcon, LogOutIcon, MenuIcon, XIcon, ShieldCheckIcon, BuildingIcon, SettingsIcon, ScrollTextIcon, ReceiptIcon, PackageIcon, BoxIcon, TruckIcon, CalendarDaysIcon, FileOutputIcon, RotateCcwIcon, TagIcon, PercentIcon, SendIcon, ClipboardPlusIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../types';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  permission: keyof Permissions | 'settings' | 'bitacora' | 'cuentas_cobrar' | 'pedidos' | 'inventario' | 'caja_vacia' | 'facturas_dejadas' | 'devoluciones' | 'ofertas' | 'comisiones' | 'conduces' | 'compras';
}
// Group definitions
const navGroups = [{
  label: 'GESTIÓN',
  items: ['dashboard', 'visits', 'new-visit', 'planificacion']
}, {
  label: 'COMERCIAL',
  items: ['collections', 'sales', 'pedidos', 'ofertas', 'comisiones']
}, {
  label: 'OPERACIONES',
  items: ['cuentas-cobrar', 'conduces', 'facturas-dejadas', 'devoluciones', 'compras', 'inventario', 'caja-vacia']
}, {
  label: 'ANÁLISIS',
  items: ['clients', 'reports', 'map']
}, {
  label: 'SISTEMA',
  items: ['users', 'roles', 'bitacora']
}];
const navItems: NavItem[] = [{
  id: 'dashboard',
  label: 'Panel Principal',
  icon: <LayoutDashboardIcon className="w-5 h-5" data-id="element-145" />,
  permission: 'dashboard'
}, {
  id: 'visits',
  label: 'Mis Visitas',
  icon: <ClipboardListIcon className="w-5 h-5" data-id="element-146" />,
  permission: 'visits'
}, {
  id: 'new-visit',
  label: 'Nueva Visita',
  icon: <MapPinIcon className="w-5 h-5" data-id="element-147" />,
  permission: 'new-visit'
}, {
  id: 'planificacion',
  label: 'Planificación',
  icon: <CalendarDaysIcon className="w-5 h-5" data-id="element-148" />,
  permission: 'planificacion'
}, {
  id: 'collections',
  label: 'Cobros',
  icon: <DollarSignIcon className="w-5 h-5" data-id="element-149" />,
  permission: 'collections'
}, {
  id: 'sales',
  label: 'Ventas',
  icon: <ShoppingCartIcon className="w-5 h-5" data-id="element-150" />,
  permission: 'sales'
}, {
  id: 'pedidos',
  label: 'Pedidos',
  icon: <TruckIcon className="w-5 h-5" data-id="element-151" />,
  permission: 'pedidos'
}, {
  id: 'ofertas',
  label: 'Ofertas e Impulsos',
  icon: <TagIcon className="w-5 h-5" data-id="element-152" />,
  permission: 'ofertas'
}, {
  id: 'comisiones',
  label: 'Comisiones',
  icon: <PercentIcon className="w-5 h-5" data-id="element-153" />,
  permission: 'comisiones'
}, {
  id: 'conduces',
  label: 'Conduces',
  icon: <SendIcon className="w-5 h-5" data-id="element-154" />,
  permission: 'conduces'
}, {
  id: 'cuentas-cobrar',
  label: 'Cuentas x Cobrar',
  icon: <ReceiptIcon className="w-5 h-5" data-id="element-155" />,
  permission: 'cuentas_cobrar'
}, {
  id: 'facturas-dejadas',
  label: 'Facturas Depositadas',
  icon: <FileOutputIcon className="w-5 h-5" data-id="element-156" />,
  permission: 'facturas_dejadas'
}, {
  id: 'devoluciones',
  label: 'Devoluciones',
  icon: <RotateCcwIcon className="w-5 h-5" data-id="element-157" />,
  permission: 'devoluciones'
}, {
  id: 'compras',
  label: 'Compras',
  icon: <ClipboardPlusIcon className="w-5 h-5" data-id="element-158" />,
  permission: 'compras'
}, {
  id: 'inventario',
  label: 'Inventario',
  icon: <PackageIcon className="w-5 h-5" data-id="element-159" />,
  permission: 'inventario'
}, {
  id: 'caja-vacia',
  label: 'Caja Vacía',
  icon: <BoxIcon className="w-5 h-5" data-id="element-160" />,
  permission: 'caja_vacia'
}, {
  id: 'clients',
  label: 'Farmacias',
  icon: <BuildingIcon className="w-5 h-5" data-id="element-161" />,
  permission: 'clients'
}, {
  id: 'reports',
  label: 'Reportes',
  icon: <FileTextIcon className="w-5 h-5" data-id="element-162" />,
  permission: 'reports'
}, {
  id: 'map',
  label: 'Mapa GPS',
  icon: <MapIcon className="w-5 h-5" data-id="element-163" />,
  permission: 'map'
}, {
  id: 'users',
  label: 'Usuarios',
  icon: <UsersIcon className="w-5 h-5" data-id="element-164" />,
  permission: 'users'
}, {
  id: 'roles',
  label: 'Roles y Permisos',
  icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-165" />,
  permission: 'roles'
}, {
  id: 'bitacora',
  label: 'Bitácora',
  icon: <ScrollTextIcon className="w-5 h-5" data-id="element-166" />,
  permission: 'bitacora'
}];
export function Sidebar({
  currentPage,
  onNavigate,
  isOpen,
  onToggle
}: SidebarProps) {
  const {
    user,
    logout
  } = useAuth();
  const filteredItems = navItems.filter(item => {
    if (!user) return false;
    if (item.permission === 'clients') {
      return user.rolNombre === 'Administrador' || user.permisos?.users === true || user.permisos?.clients === true;
    }
    if (item.permission === 'bitacora') {
      return user.rolNombre === 'Administrador' || user.permisos?.bitacora === true;
    }
    // New modules: require explicit permission (no undefined fallback)
    if (['cuentas_cobrar', 'pedidos', 'inventario', 'caja_vacia', 'facturas_dejadas', 'devoluciones', 'ofertas', 'comisiones', 'conduces', 'compras', 'planificacion', 'descuento_pronto_pago', 'sales', 'inventario_editar', 'pedidos_editar'].includes(item.permission)) {
      const permKey = item.permission as keyof Permissions;
      return user.rolNombre === 'Administrador' || user.permisos?.[permKey] === true;
    }
    return user.permisos[item.permission as keyof Permissions] === true;
  });
  return <>
      {isOpen && <div className="fixed inset-0 bg-brand-950/70 backdrop-blur-sm z-40 lg:hidden" onClick={onToggle} data-id="element-167" />}

      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-64
        flex flex-col
        transform transition-transform duration-300 ease-out
        lg:sticky lg:top-0 lg:z-auto lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        shadow-2xl
      `} style={{
      background: 'linear-gradient(180deg, #0d1332 0%, #111a4a 100%)'
    }} data-id="element-168">
        {/* Logo area */}
        <div className="flex items-center justify-between h-20 px-5 border-b border-white/10 flex-shrink-0 bg-[#0d1332]/50 backdrop-blur-sm" data-id="element-169">
          <button onClick={() => {
          onNavigate('dashboard');
          if (window.innerWidth < 1024) onToggle();
        }} className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-170">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/50 flex-shrink-0 overflow-hidden p-1.5" data-id="element-171">
              <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-172" />
            </div>
            <div className="min-w-0 text-left" data-id="element-173">
              <p className="text-white font-bold text-sm leading-tight truncate font-heading" data-id="element-174">
                Miladys Rodríguez
              </p>
              <p className="text-[11px] font-medium truncate opacity-80" style={{
              color: '#aac1ef'
            }} data-id="element-175">
                & Asociados
              </p>
            </div>
          </button>
          <button onClick={onToggle} className="lg:hidden p-1.5 hover:bg-white/10 rounded-lg text-white/60 flex-shrink-0 transition-colors" data-id="element-176">
            <XIcon className="w-5 h-5" data-id="element-177" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto custom-scrollbar" data-id="element-178">
          {navGroups.map(group => {
          const groupItems = filteredItems.filter(item => group.items.includes(item.id));
          if (groupItems.length === 0) return null;
          return <div key={group.label} className="space-y-1" data-id="element-179">
                <div className="px-3 mb-2" data-id="element-180">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider opacity-70" data-id="element-181">
                    {group.label}
                  </p>
                </div>
                {groupItems.map(item => {
              const isActive = currentPage === item.id;
              return <button key={item.id} onClick={() => {
                onNavigate(item.id);
                if (window.innerWidth < 1024) onToggle();
              }} className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                        transition-all duration-200 relative group
                        ${isActive ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
                      `} style={isActive ? {
                background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
              } : {}} data-id="element-182">
                      <span className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-183">
                        {item.icon}
                      </span>
                      {item.label}
                      {isActive && <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-300 shadow-[0_0_8px_rgba(147,197,253,0.6)]" data-id="element-184" />}
                    </button>;
            })}
              </div>;
        })}
        </nav>

        {/* Bottom actions */}
        <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-1 bg-[#0d1332]/30 backdrop-blur-md" data-id="element-185">
          <button onClick={() => {
          onNavigate('settings');
          if (window.innerWidth < 1024) onToggle();
        }} className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
              transition-all duration-200 relative
              ${currentPage === 'settings' ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
            `} style={currentPage === 'settings' ? {
          background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
        } : {}} data-id="element-186">
            <SettingsIcon className="w-5 h-5" data-id="element-187" />
            Configuración
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:bg-rose-500/20 hover:text-rose-200 transition-all duration-200" data-id="element-188">
            <LogOutIcon className="w-5 h-5" data-id="element-189" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      <button onClick={onToggle} className="fixed top-4 left-4 z-30 lg:hidden p-2.5 text-white rounded-xl shadow-lg hover:scale-105 transition-transform" style={{
      backgroundColor: '#0d1332'
    }} aria-label="Abrir menú" data-id="element-190">
        <MenuIcon className="w-5 h-5" data-id="element-191" />
      </button>
    </>;
}